import React, { useMemo } from 'react';
import { selectedAccountSelector, selectedAccStateSelector } from '@core/store/library-stores';
import { useAppSelector } from '@core/store/store';
import { usePMZoneUpdateInfoDataSource } from '@features/dataSource';
import { LabeledValue, LoadingData, useFormatDate } from '@hwm/ui-lib';
import { t, Trans } from '@lingui/macro';
import { Fade, Typography } from '@material-ui/core';
import { ContentWrapper, SitesList } from '@shared/components';
import PegasusTables from '@shared/components/PegasusTables';
import { isEmpty } from 'lodash';
import { formatPegasusStatus } from './PMZoneDetails.utils';
import { AccountModel } from '@shared/models';

const tailwindClasses = {
  section: 'mx-0.5 mb-4 px-2',
};

export const PMZoneDetails: React.FC<{
  /**
   * `dataTestId` - name of the PM Zone Details (preferably `<name>-pmz-details`)
   * @example
   * ```tsx
   * <PMZoneDetails dataTestId="test-pmz-details" />
   */
  dataTestId: string;
}> = ({ dataTestId }) => {
  const { id, isPmZone } = useAppSelector(selectedAccountSelector);
  const { pmZoneData } = useAppSelector(selectedAccStateSelector);

  const { formatDate } = useFormatDate();

  const { pmZoneUpdateInfo } = usePMZoneUpdateInfoDataSource(id, isPmZone);

  const { name, description, pegasusStatus, pegasusFlowStatuses, pegasusTimeStatuses } = pmZoneData ?? {};

  const hierarchy = useAppSelector((state: any) => state?.accounts?.hierarchy as AccountModel[]) ?? [];

  const { isFlowType, isTimeType, isTimeInterpolateType } = pegasusStatus ?? {};
  const formattedPegasus = formatPegasusStatus(pegasusStatus, formatDate);

  const showTimePegasusTable = useMemo(
    () => (isTimeType || isTimeInterpolateType) && !isEmpty(pegasusTimeStatuses),
    [isTimeType, isTimeInterpolateType, pegasusTimeStatuses]
  );

  const showFlowPegasusTable = useMemo(
    () => isFlowType && !isEmpty(pegasusFlowStatuses),
    [isFlowType, pegasusFlowStatuses]
  );

  return (
    <div className="flex flex-col justify-start h-full w-full overflow-hidden" data-testid={`${dataTestId}-wrapper`}>
      <div className="flex flex-col h-full max-h-full overflow-y-auto" data-testid={`${dataTestId}-inner-wrapper`}>
        {!pmZoneData ? (
          <LoadingData dataTestId={`${dataTestId}-loading`} />
        ) : (
          <Fade in={Boolean(pmZoneData)} {...{ timeout: 800 }}>
            <div className="flex flex-col h-full overflow-y-auto">
              <div className="mx-0.5 px-2">
                <LabeledValue
                  label={t({
                    message: `PMZ Name`,
                    id: '[PMZName]',
                    comment: 'Abbreviation from `Pressure Monitoring Zone`',
                  })}
                  dataTestId={`${dataTestId}-pmz-name`}
                >
                  {name}
                </LabeledValue>
                {hierarchy?.length > 1 ? (
                  <LabeledValue label={t`Parent Account`} dataTestId={`${dataTestId}-parent-account`}>
                    {hierarchy[hierarchy.length - 2].name}
                  </LabeledValue>
                ) : null}
                <LabeledValue label={t`Description`} dataTestId={`${dataTestId}-description`}>
                  {description}
                </LabeledValue>
              </div>
              <ContentWrapper
                title={t({
                  message: 'PMZ sites',
                  id: '[PMZDetailsSection.PMZoneSites]',
                })}
                dataTestId={`${dataTestId}-pmz-sites-content-wrapper`}
              >
                <SitesList minHeight={80} dataTestId={`${dataTestId}-pmz-sites`} />
              </ContentWrapper>
              {(showTimePegasusTable || showFlowPegasusTable) && (
                <ContentWrapper
                  title={`${t`Current Control`} ${showTimePegasusTable && showFlowPegasusTable ? t`tables` : t`table`}`}
                  dataTestId={`${dataTestId}-pegasus-content-wrapper`}
                >
                  <PegasusTables displayAsColBreakpoint="xl" isForPmzDetails dataTestId={`${dataTestId}-pegasus`} />
                </ContentWrapper>
              )}
              <div className={`flex flex-col md:flex-row ${tailwindClasses.section}`}>
                {formattedPegasus && (
                  <div
                    className="flex flex-col w-full md:w-1/2 mr-4 mb-2 md:mb-0"
                    data-testid={`${dataTestId}-pegasus-status-wrapper`}
                  >
                    <Typography variant="h6" data-testid={`${dataTestId}-controller-status-label`}>
                      <Trans>Controller Status</Trans>:
                    </Typography>
                    {formattedPegasus.map((row, i) =>
                      row ? (
                        <p key={i} data-testid={`${dataTestId}-pegasus-status-row-${i}`}>
                          {row}
                        </p>
                      ) : null
                    )}
                  </div>
                )}
                <div className={`w-full ${formattedPegasus ? 'md:w-1/2' : ''}`}>
                  <LabeledValue
                    label={t({
                      message: 'Last Table Update',
                      id: '[PMZDetails.LastTableUpdate]',
                    })}
                    tooltipText={t`Last table update time/date only applies when the controller is in Learning or Active mode`}
                    dataTestId={`${dataTestId}-last-table-update`}
                  >
                    {pmZoneUpdateInfo?.lastTableUpdate
                      ? formatDate(pmZoneUpdateInfo.lastTableUpdate, 'P p')
                      : t`No value`}
                  </LabeledValue>
                  <LabeledValue
                    label={t({
                      message: 'Status Last Check',
                      id: '[PMZDetails.StatusLastCheck]',
                    })}
                    dataTestId={`${dataTestId}-status-last-check`}
                  >
                    {pmZoneUpdateInfo?.statusLastCheck
                      ? formatDate(pmZoneUpdateInfo.statusLastCheck, 'P p')
                      : t`No value`}
                  </LabeledValue>
                </div>
              </div>
            </div>
          </Fade>
        )}
      </div>
    </div>
  );
};
